/* eslint-disable react-hooks/exhaustive-deps */
import { Checkbox, Tooltip } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { addEdge, removeElements } from "react-flow-renderer";
import styled from 'styled-components';
import { typesConfigs } from "../../../../../../../constants/types";
import { formatClassname } from 'datastake-daf/src/helpers/ClassesHelper';
import { fitView } from "../../../../../../../helpers/flow";

export const initializeData = (data) => {
  const items = [];

  const addItem = (item, level, parent, isOwnData) => {
    const sources = item.sources;
    let targets = [];

    if (parent) {
      targets = [parent];
    } else {
      targets = item.children ? item.children.map((ch) => ch.id) : [];
    }
    items.push({
      type: item.type,
      id: item.id,
      label: item.name,
      title: item.title,
      datastakeId: item.datastakeId,
      namespace: item.namespace,
      sources,
      targets,
      level,
      isOwnData,
      otherSources: item.otherSources,
      isNotConnected: item.isNotConnected,
    });
  }

  const addChildren = (children, level, parent) => {
    children.forEach((child) => {
      addItem(child, level, parent);
      if (child.children) {
        addChildren(child.children, level + 1);
      }

      if (child.parents) {
        addChildren(child.parents, level - 1, child.id);
      }
    });
  };

  // const ownDataIsValid = checkIfIsValid(data.sources);

  // if (ownDataIsValid) {

  addItem(data, 0, undefined, true);

  if (data.children) {
    addChildren(data.children, 1);
  }

  if (data.parents) {
    addChildren(data.parents, -1, data.id);
  }

  const levels = items.map((i) => i.level);
  const min = Math.min(...levels);
  const max = Math.max(...levels);

  const sections = [];
  for (let i = min; i <= max; i++) {
    const section = items.filter((it) => it.level === i);
    sections.push(section);
  }

  return sections;
  // }
  // return [];
};

export const sourcesColors = {
  ownData: '#2E90FA',
  supplyChainActors: '#52C41A',
  informationSources: '#FF7875',
};

const isSource = (id, con) => {
  const sourceCon = con.find((c) => c.source === id);
  if (sourceCon) {
    return true;
  }
  return false;
};

const isTarget = (id, con) => {
  const targetCon = con.find((c) => c.target === id);
  if (targetCon) {
    return true;
  }
  return false;
};

const InnerStyle = styled.div`
  display: flex;
  flex-direction: column;

  label {
    text-align: left;
    margin: 0px;
  }

  .header-cont {
    opacity: 0.4;
    display: flex;
    margin-bottom: 0px !important;
    padding: 8px 6px;
    transition: .4s opacity;

    &.active {
      opacity: 1;
    }

    .checkbox-c {
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .ant-checkbox {
      transform: scale(0.7);
    }

    .header {
      display: flex;
      flex-direction: column;
      flex: 1;

      .type {
        font-size: 6px;
        font-weight: 700;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 110px;
      }

      .label {
        font-size: 9px;
        font-weight: 500;
        color: #101828;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 110px;
      }
    }
  }
`;

const Header = ({
  config,
  isActive = false,
  onDisable = () => { },
  onUnDisable = () => { },
}) => {
  const headerConfig = useMemo(() =>
    typesConfigs.find((c) => c.type === config.type), [config]);

  return headerConfig ? (
    <div className={formatClassname(["header-cont", isActive && 'active'])}>
      <div className="header">
        <Tooltip title={config.label}>
          <div style={{
            width: '100%',
            background: '#fff',
            textAlign: 'start',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flexStart',
            alignItems: 'center',
            flexWrap: 'nowrap'
          }}>
            <label className="label">
              {config.label}
            </label>
          </div>
        </Tooltip>
        <Tooltip title={config.title}>
          <div style={{
            width: '100%',
            background: '#fff',
            textAlign: 'start',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flexStart',
            alignItems: 'center',
            flexWrap: 'nowrap'
          }}>
            <label className="type" style={{ color: headerConfig.color, wordBreak: 'break-all' }}>
              {config.title}
            </label>
          </div>
        </Tooltip>
      </div>
      <div className="checkbox-c">
        <Checkbox
          checked={isActive}
          onChange={(e) => {
            if (e.target.checked) {
              onUnDisable(config.id);
            } else {
              onDisable(config.id);
            }
          }}
        />
      </div>
    </div>
  ) : null;
};

export function useNodes({
  data,
  flow = 'LR',
  disabledIds = [],
  setDisabledIds = () => { },
  formValue = {},
}) {
  const [X, Y] = flow === 'LR' ? [250, -110] : [-200, 180];
  const [initElements, setInitElements] = useState();
  const [elements, setElements] = useState([]);
  const [instance, setInstance] = useState(null);
  const [visualizeSelected, setVisualizeSelected] = useState(
    typeof formValue.visualizeSelected === 'boolean' ?
      formValue.visualizeSelected
      : true
  );
  const [focusSelected, setFocusSelected] = useState(
    typeof formValue.focusSelected === 'boolean' ?
      formValue.focusSelected
      : false
  );

  const reset = () => {
    if (instance) {
      setTimeout(() => fitView(instance.fitView, 1), 100);
    }
  };

  const items = useMemo(() => {
    if (data && Object.keys(data).length) {
      return initializeData(data);
    }
    return null;
  }, [data]);

  const allItems = useMemo(() => {
    if (items) {
      const all = [];
      items.forEach((i) => i.forEach((a) => {
        all.push(a);
      }));
      return all;
    }

    return null;
  }, [items]);

  const onElementsRemove = (elementsToRemove) =>
    setElements((els) => removeElements(elementsToRemove, els));

  const onConnect = (params) => setElements((els) => addEdge({
    ...params, type: 'buttonedge',
  }, els));

  const onLoad = (reactFlowInstance) => {
    setTimeout(() => fitView(reactFlowInstance.fitView, 1), 100);
    setInstance(reactFlowInstance);
  };

  const setAllPositions = (nodes) => {
    const groupedByLevel = {};

    nodes.forEach((n) => {
      if (groupedByLevel[n.level]) {
        groupedByLevel[n.level].push(n);
      } else {
        groupedByLevel[n.level] = [n];
      }
    });

    const elements = [];
    Object.entries(groupedByLevel).forEach(([k, v]) => {
      let group = [];
      if (flow === 'LR') {
        const maxChildY = ((v.length + 1) * (Y / 2));
        const prevGroupedByLevel = groupedByLevel[k - 1];
        const parentX = prevGroupedByLevel ? prevGroupedByLevel[0].position.x : 0;
        v.forEach((el, i) => {
          group.push({
            ...el,
            position: {
              x: parentX + X,
              y: maxChildY - (Y * (i + 1)),
            },
          });
        });
        elements.push(...group);
        groupedByLevel[k] = group;
      } else {
        const maxChildX = ((v.length + 1) * (X / 2));
        const prevGroupedByLevel = groupedByLevel[k - 1];
        const parentY = prevGroupedByLevel ? prevGroupedByLevel[0].position.y : 0;
        v.forEach((el, i) => {
          group.push({
            ...el,
            position: {
              y: parentY + Y,
              x: maxChildX - (X * (i + 1)),
            },
          });
        });
        elements.push(...group);
        groupedByLevel[k] = group;
      }
    });

    return elements;
  };

  const initializeItems = (exclude = []) => {
    let nodes = [];
    const con = [];
    const data = items;

    data.forEach((items, i) => {
      items.forEach((item) => {
        if (exclude.includes(item.id)) {
          return;
        }

        if (item.targets) {
          item.targets.forEach((target) => {
            if (allItems.find((i) => i.id === target)) {
              const last = con.find((c) => c.target === target && c.source === item.id);
              if (!last && !item.isNotConnected) {
                con.push({
                  source: item.id,
                  target: target,
                });
              }
            }
          })
        }

        if (!nodes.find((n) => n.id === item.id)) {
          nodes.push({
            id: item.id,
            sourcePosition: flow === 'LR' ? 'left' : 'bottom',
            position: { x: 0, y: 0 },
            type: 'node',
            config: {
              ...item,
              isSource: isSource(item.id, con),
              isTarget: isTarget(item.id, con),
            },
            level: i,
          });
        }
      });
    });

    let edges = [];
    con.forEach(({ source, target, isInfoSource, isOwnData, isSupplyChainActor }) => {
      edges.push({
        id: `${source}-${target}`,
        source,
        target,
        isInfoSource,
        type: 'edge',
        isOwnData,
        isSupplyChainActor
      });
    });

    nodes = setAllPositions(nodes);

    setInitElements([
      ...nodes,
      ...edges,
    ]);
  };

  const getStroke = (el) => {
    if (el.isOwnData) {
      return '#113366';
    }
    if (el.isSupplyChainActor) {
      return sourcesColors.supplyChainActors;
    }
    if (el.isInfoSource) {
      return sourcesColors.informationSources;
    }

    return '#113366';
  }

  const onDisable = (id) => {
    if (!Array.isArray(initElements)) {
      return;
    }

    const element = initElements.find((e) => e.id === id);
    const ownData = initElements.find((e) => e.id === data.id);

    if (element && ownData) {
      if (element.id === ownData.id) {
        return;
      }

      if (element.level < ownData.level) {
        const disabled = [id];

        const check = (el = {}) => {
          const _sorted = initElements
            .filter((e) => e.level === el.level - 1)
            .sort((a, b) => {
              const aVal = a.config ? (a.config.targets || []).includes(el.id) ? 1 : -1 : -1;
              const bVal = b.config ? (b.config.targets || []).includes(el.id) ? 1 : -1 : -1;
              return bVal - aVal;
            });

          const isInDisabled = (targets = []) => {
            let isValid = false;

            targets.forEach((t) => {
              if (isValid) {
                return;
              }

              if (disabled.includes(t)) {
                isValid = true;
              }
            })
            return isValid;
          };

          const next = _sorted.filter((e) => {
            if (e.config && e.config.targets && isInDisabled(e.config.targets)) {
              disabled.push(e.id);
              return true;
            }

            if (
              (e.config.targets.includes(el.id)) &&
              !e.config.targets.filter((e) => {
                if (disabled.includes(e)) {
                  return false;
                }

                const _element = initElements.find((el) => el.id === e);

                if (_element && !_element.disabled) {
                  return true;
                }
                return false;
              }).length
            ) {
              disabled.push(e.id);
              return true;
            }

            return false;
          });

          if (next.length) {
            next.forEach((n) => {
              check(n);
            });
          }
        }

        check(element);
        setDisabledIds((prev) => [...prev, ...disabled]);
      } else {
        const disabled = [id];

        const check = (el = {}) => {

          const next = initElements.filter((e) => (
            e.level === el.level + 1 &&
            el.config.targets.includes(e.id)
          ));

          if (next.length) {
            next.forEach((n) => {
              disabled.push(n.id);
              check(n);
            });
          }
        }

        check(element);
        setDisabledIds((prev) => [...prev, ...disabled]);
      }
    }
  };

  const onUnDisable = (id) => {
    if (!Array.isArray(initElements)) {
      return;
    }

    const element = initElements.find((e) => e.id === id);
    const ownData = initElements.find((e) => e.id === data.id);

    if (element && ownData) {
      if (element.id === ownData.id) {
        return;
      }

      if (element.level < ownData.level) {
        let isValid = false;

        if (Array.isArray(element.config.targets) && element.config.targets.length) {
          element.config.targets.forEach((t) => {
            if (isValid) {
              return;
            }

            if (!disabledIds.includes(t)) {
              isValid = true;
            }
          })
        } else {
          isValid = true;
        }

        if (!isValid) {
          initElements.forEach((e) => {
            if (isValid) {
              return;
            }
            if (e.type === 'edge' && e.source === element.id) {
              if (!disabledIds.includes(e.target)) {
                isValid = true;
              }
            }
          })
        }


        if (isValid) {
          const disabled = [id];

          const check = (el = {}) => {
            const _sorted = initElements
              .filter((e) => e.level === el.level - 1)
              .sort((a, b) => {
                const aVal = a.config ? (a.config.targets || []).includes(el.id) ? 1 : -1 : -1;
                const bVal = b.config ? (b.config.targets || []).includes(el.id) ? 1 : -1 : -1;
                return bVal - aVal;
              });

            const isInDisabled = (targets = []) => {
              let isValid = false;

              targets.forEach((t) => {
                if (isValid) {
                  return;
                }

                if (disabled.includes(t)) {
                  isValid = true;
                }
              })
              return isValid;
            };

            const next = _sorted.filter((e) => {
              if (e.config && e.config.targets && isInDisabled(e.config.targets)) {
                disabled.push(e.id);
                return true;
              }

              if (
                (e.config.targets.includes(el.id)) &&
                !e.config.targets.filter((e) => {
                  if (disabled.includes(e)) {
                    return false;
                  }

                  const _element = initElements.find((el) => el.id === e);

                  if (_element && !_element.disabled) {
                    return true;
                  }
                  return false;
                }).length
              ) {
                disabled.push(e.id);
                return true;
              }

              return false;
            });

            if (next.length) {
              next.forEach((n) => {
                check(n);
              });
            }
          }

          check(element);
          // setDisabledIds((prev) => prev.filter(p => p !== id));
          setDisabledIds((prev) => prev.filter(p => !disabled.includes(p)));
        }
      } else {
        let isValid = false;

        if (!isValid) {
          initElements.forEach((e) => {
            if (isValid) {
              return;
            }
            if (e.type === 'edge' && e.target === element.id) {
              if (!disabledIds.includes(e.source)) {
                isValid = true;
              }
            }
          })
        }


        if (isValid) {
          const disabled = [id];

          const check = (el = {}) => {
            const next = initElements.filter((e) => (
              e.level === el.level + 1 &&
              el.config.targets.includes(e.id)
            ));

            if (next.length) {
              next.forEach((n) => {
                disabled.push(n.id);
                check(n);
              });
            }
          }

          // setDisabledIds((prev) => prev.filter(p => p !== id));
          check(element);
          setDisabledIds((prev) => prev.filter(p => !disabled.includes(p)));
        }
      }
    }
  };

  useEffect(() => {
    if (initElements) {
      let _elements = initElements;

      if (!visualizeSelected) {
        _elements = initElements.filter((i) => {
          if (i.type === 'edge') {
            return !disabledIds.includes(i.source) && !disabledIds.includes(i.target);
          }
          return !disabledIds.includes(i.id);
        });
      }

      const _values = _elements.map((el) => {
        switch (el.type) {
          case 'edge':
            const _disabled = (disabledIds.includes(el.source) || disabledIds.includes(el.target));

            return {
              id: el.id,
              source: el.source,
              target: el.target,
              style: {
                stroke: getStroke(el),
                strokeWidth: 1,
                opacity: _disabled ? 0.4 : 1,
              }
            };
          case 'node':
            return {
              id: el.id,
              data: {
                label: (
                  <>
                    <InnerStyle onClick={() => { }}>
                      <Header
                        config={el.config}
                        isActive={!disabledIds.includes(el.id)}
                        onDisable={onDisable}
                        onUnDisable={onUnDisable}
                      />
                    </InnerStyle>
                  </>
                ),
              },
              className: 'node',
              position: el.position,
              sourcePosition: flow === 'LR' ? 'right' : 'bottom',
              targetPosition: flow === 'LR' ? 'left' : 'top',
              style: {
                padding: 0,
              },
            };
          default:
            return null;
        }
      });

      setElements(_values);
    }
  }, [initElements, visualizeSelected, disabledIds]);

  useEffect(() => {
    if (items) {
      if (!visualizeSelected && focusSelected) {
        initializeItems(disabledIds);
      } else {
        initializeItems();
      }
    }
  }, [items, flow, disabledIds, focusSelected, visualizeSelected]);

  useEffect(() => {
    if (instance) {
      setTimeout(() => fitView(instance.fitView, 1), 100);
    }
  }, [flow, data, focusSelected, visualizeSelected]);

  return {
    elements,
    initElements,
    onElementsRemove,
    onConnect,
    onLoad,
    focusSelected,
    setFocusSelected,
    visualizeSelected,
    setVisualizeSelected,
    reset,
    disabledIds,
  };
}
